import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { cn } from "@utils";
import { useNavigationFilter } from "./useNavigationFilter";

/**
 * Find the first navigable route in an item tree
 */
function findFirstRoute(item) {
  if (item.to) return item.to;
  if (item.children?.length > 0) {
    for (const child of item.children) {
      const route = findFirstRoute(child);
      if (route) return route;
    }
  }
  return null;
}

/**
 * Check if current path matches any route in the item tree (including dynamic segments)
 */
function isOnChildRoute(item, currentPath) {
  if (item.to) {
    // Check exact match or if current path starts with this route (for dynamic segments like /accounts/:groupId)
    if (currentPath === item.to || currentPath.startsWith(item.to + "/")) {
      return true;
    }
  }
  if (item.children?.length > 0) {
    for (const child of item.children) {
      if (isOnChildRoute(child, currentPath)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Recursive component for nested nav items (children without icons)
 */
function NavSubItem({ item, onNavigate, onAction }) {
  const { shouldShow } = useNavigationFilter();
  const hasChildren = item.children && item.children.length > 0;

  if (!hasChildren) {
    // Action button item
    if (item.action) {
      return (
        <li>
          <div className="w-full pl-[11px]">
            <button
              type="button"
              onClick={() => onAction?.(item.action)}
              className="flex items-center w-full rounded-md px-2 py-1.5 text-sm/5 font-medium text-primary-500 hover:bg-zinc-200"
            >
              <item.icon
                aria-hidden="true"
                className="shrink-0 inline mr-1.5"
              />
              {item.name}
            </button>
          </div>
        </li>
      );
    }

    // Simple link item (with optional subAction)
    return (
      <li>
        <div className="flex w-full items-center pl-[11px] group">
          <NavLink
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex w-full rounded-md px-2 py-1.5 text-sm/5 font-medium",
                isActive ? "bg-zinc-200" : "hover:bg-zinc-200",
              )
            }
          >
            <span className="flex items-center gap-1 min-w-0 grow">
              <span className="truncate" title={item.name}>
                {item.name}
              </span>
              {item.count != null && (
                <span className="shrink-0">· {item.count}</span>
              )}
            </span>
            {item.subAction && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onAction?.(item.subAction.action, item);
                }}
                className="rounded-md text-primary-500 hidden group-hover:block shrink-0 ml-2"
              >
                <item.subAction.icon aria-hidden="true" className="size-4" />
              </button>
            )}
          </NavLink>
        </div>
      </li>
    );
  }

  const hasNavigableChildren = item.children.some(
    (child) => child.to || child.children,
  );

  return (
    <li>
      <Disclosure as="div" defaultOpen>
        <div className="flex w-full items-center pl-[11px]">
          {item.to ? (
            <NavLink
              to={item.to}
              end={hasNavigableChildren}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex-1 rounded-md px-2 py-1.5 text-sm/5 font-medium",
                  isActive ? "bg-zinc-200" : "hover:bg-zinc-200",
                )
              }
            >
              <span className="flex items-center gap-1 min-w-0 flex-1">
                <span className="truncate" title={item.name}>
                  {item.name}
                </span>
                {item.count != null && (
                  <span className="shrink-0">· {item.count}</span>
                )}
              </span>
            </NavLink>
          ) : (
            <span
              className="flex-1 px-2 py-1.5 text-sm/5 font-medium truncate"
              title={item.name}
            >
              {item.name}
            </span>
          )}
        </div>
        <DisclosurePanel
          as="ul"
          className="mt-px ml-3.5 flex flex-col gap-px border-zinc-300 my-px"
        >
          {item.children.filter(shouldShow).map((child) => (
            <NavSubItem
              key={child.id ?? child.name}
              item={child}
              onNavigate={onNavigate}
              onAction={onAction}
            />
          ))}
        </DisclosurePanel>
      </Disclosure>
    </li>
  );
}

/**
 * Icon in a fixed slot. The count sits in the icon's top-right corner while collapsed (fading in
 * as the inline pill sweeps away with the width) so it stays present in both layouts — the icon
 * and its badge never unmount, only the label/pill get clipped by the rail width.
 */
function NavItemIcon({ item, collapsed, active = false }) {
  return (
    <span className="relative flex shrink-0 items-center justify-center size-5">
      {/* Collapsed active/hover highlight: a compact chip centred on the icon rather than the
          full-width bar, so it reads as centred in the rail. */}
      {collapsed && (
        <span
          className={cn(
            "absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 rounded-md",
            active ? "bg-zinc-200" : "group-hover:bg-zinc-200",
          )}
        />
      )}
      <item.icon aria-hidden="true" className="relative size-3.5" />
      {item.count != null && (
        <span
          className={cn(
            "absolute -top-1.5 -right-1.5 z-10 inline-flex items-center justify-center min-w-[15px] h-[15px] px-[3px] rounded-full bg-primary-500 text-white text-[9px] font-semibold leading-none transition-opacity duration-200",
            collapsed ? "opacity-100" : "opacity-0",
          )}
        >
          {item.count > 99 ? "99+" : item.count}
        </span>
      )}
    </span>
  );
}

function NavItemPill({ item }) {
  if (item.count == null) return null;
  return (
    <span className="shrink-0 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary-500 text-white text-[10px] font-semibold leading-none">
      {item.count > 99 ? "99+" : item.count}
    </span>
  );
}

/**
 * Top-level nav item with icon (handles both simple links and items with children)
 */
export function NavItem({ item, onNavigate, onAction, collapsed = false }) {
  const { shouldShow } = useNavigationFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;

  // Simple link item (no children)
  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        onClick={onNavigate}
        title={collapsed ? item.name : undefined}
        className={({ isActive }) =>
          cn(
            "group flex items-center gap-x-3 rounded-md px-2 py-1.5 text-sm/5 font-medium",
            !collapsed && (isActive ? "bg-zinc-200" : "hover:bg-zinc-200"),
          )
        }
      >
        {({ isActive }) => (
          <>
            <NavItemIcon item={item} collapsed={collapsed} active={isActive} />
            <span className="flex min-w-0 flex-1 items-center gap-x-2 overflow-hidden whitespace-nowrap">
              <span className="flex-1 truncate" title={item.name}>
                {item.name}
              </span>
              <NavItemPill item={item} />
            </span>
          </>
        )}
      </NavLink>
    );
  }

  const alreadyOnChildRoute = isOnChildRoute(item, location.pathname);

  // Collapsed: no room for the disclosure panel, so a click navigates to the current/first child
  // route. Expanded: toggle the panel, jumping to the first child if we're not already on it.
  const handleButtonClick = (open) => {
    if (collapsed) {
      const target = alreadyOnChildRoute
        ? location.pathname
        : findFirstRoute(item);
      if (target) {
        navigate(target);
        onNavigate?.();
      }
      return;
    }
    if (!open && !alreadyOnChildRoute) {
      const firstRoute = findFirstRoute(item.children[0]);
      if (firstRoute) {
        navigate(firstRoute);
      }
    }
  };

  return (
    <Disclosure as="div">
      {({ open }) => (
        <>
          <DisclosureButton
            onClick={() => handleButtonClick(open)}
            title={collapsed ? item.name : undefined}
            className={cn(
              "group flex w-full items-center gap-x-3 rounded-md px-2 py-1.5 text-left text-sm/5 font-medium",
              !collapsed && "hover:bg-zinc-200",
            )}
          >
            <NavItemIcon
              item={item}
              collapsed={collapsed}
              active={alreadyOnChildRoute}
            />
            <span className="flex min-w-0 flex-1 items-center gap-x-2 overflow-hidden whitespace-nowrap">
              <span className="flex-1 truncate" title={item.name}>
                {item.name}
              </span>
              <NavItemPill item={item} />
              <ChevronRightIcon
                aria-hidden="true"
                className="size-5 shrink-0 text-zinc-400 group-data-[open]:rotate-90 group-data-[open]:text-zinc-500"
              />
            </span>
          </DisclosureButton>
          {!collapsed && (
            <DisclosurePanel
              as="ul"
              className="mt-px ml-3.5 flex flex-col gap-px border-l border-zinc-300 my-px"
            >
              {item.children.filter(shouldShow).map((subItem) => (
                <NavSubItem
                  key={subItem.id ?? subItem.name}
                  item={subItem}
                  onNavigate={onNavigate}
                  onAction={onAction}
                />
              ))}
            </DisclosurePanel>
          )}
        </>
      )}
    </Disclosure>
  );
}
